import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { checkLetsLaunchAccess } from '../../client/api';
import { useUserContext } from '../../context/UserContext';

// SpikeUp Solutions Ops Command brand system - see spikeup-solutions-brand-book.html
const THEME_COLORS = {
  bg: '#04070a',
  panel: '#0b1318',
  panel2: '#0e1a20',
  bezel: '#1b262d',
  bezelHi: '#2c3b44',
  line: '#16242b',
  cyan: '#39e0ff',
  cyanGlow: 'rgba(57,224,255,.5)',
  txt: '#9fc6c0',
  txtDim: '#5b7a78',
  ink: '#cdeee6',
} as const;

const FONT_DISPLAY = "'Orbitron', sans-serif";
const FONT_UI = "'Rajdhani', sans-serif";
const FONT_MONO = "'Share Tech Mono', monospace";

interface AppBadge {
  text: string;
  background: string;
  color: string;
}

interface AppTile {
  key: string;
  name: string;
  logo?: string;
  badge?: AppBadge;
  onClick: () => void;
}

const AppSelection: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUserContext();
  const [apps, setApps] = useState<AppTile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const authInfoCookie = Cookies.get('authInfo');
        if (!authInfoCookie) {
          console.error('No authInfo found in cookies.');
          setLoading(false);
          return;
        }

        const { organizationKey } = JSON.parse(authInfoCookie);

        const hasLetsLaunchAccess = await checkLetsLaunchAccess(organizationKey);
        const hasZapflowAccess = !!user?.zapflowAccess;
        const hasFarmSystemAccess = !!user?.farmSystemAccess;
        // campaign-tester has no lets-launch-backend User field of its own -
        // access is enforced at its own backend (spikeup.com domain + a
        // manual email allowlist), this is just a visibility gate so the
        // tile only shows for users who'd actually get in.
        const hasCampaignTesterAccess = !!user?.email?.toLowerCase().endsWith('@spikeup.com');

        const tiles: AppTile[] = [];
        if (hasLetsLaunchAccess) {
          tiles.push({
            key: 'letslaunch',
            name: 'LetsLaunch AI Video Generator',
            logo: '/logo512.png',
            onClick: () => navigate('/'),
          });
        }
        if (hasZapflowAccess) {
          tiles.push({
            key: 'zapflow',
            name: 'ZapFlow',
            badge: { text: 'ZF', background: '#000000', color: '#22c55e' },
            onClick: () => {
              window.location.href = process.env.REACT_APP_ZAPFLOW_URL || 'https://zapflow.letslaunch.io';
            },
          });
        }
        if (hasFarmSystemAccess) {
          tiles.push({
            key: 'farmsystem',
            name: 'Farm System',
            badge: { text: 'FS', background: '#000000', color: '#a855f7' },
            onClick: () => {
              window.location.href = process.env.REACT_APP_FARMSYSTEM_URL || 'https://farmsystem.letslaunch.io';
            },
          });
        }
        if (hasCampaignTesterAccess) {
          tiles.push({
            key: 'campaigntester',
            name: 'Campaign Tester',
            badge: { text: 'CT', background: '#000000', color: THEME_COLORS.cyan },
            onClick: () => {
              window.location.href = process.env.REACT_APP_CAMPAIGNTESTER_URL || 'https://campaign-tester.letslaunch.io';
            },
          });
        }

        // Only one app accessible - skip the picker and go straight there.
        if (tiles.length === 1) {
          tiles[0].onClick();
          return;
        }

        setApps(tiles);
      } catch (error) {
        console.error('Error checking access:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: `radial-gradient(120% 80% at 50% -8%, #0a1820 0%, ${THEME_COLORS.bg} 55%), ${THEME_COLORS.bg}`,
        fontFamily: FONT_UI,
        padding: 2,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          mixBlendMode: 'overlay',
          opacity: 0.45,
          background:
            'repeating-linear-gradient(0deg, rgba(0,0,0,.35) 0 1px, transparent 1px 3px)',
        },
      }}
    >
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontFamily: FONT_DISPLAY,
          fontWeight: 900,
          letterSpacing: '3px',
          textTransform: 'uppercase',
          fontSize: 'clamp(20px, 3vw, 32px)',
          color: THEME_COLORS.ink,
          textShadow: `0 0 16px ${THEME_COLORS.cyanGlow}`,
          zIndex: 1,
        }}
      >
        Choose Your Application
      </Typography>
      <Grid container spacing={4} sx={{ mt: 2, maxWidth: 900, zIndex: 1 }} justifyContent="center">
        {apps.map((app) => (
          <Grid item xs={12} sm={6} md={4} key={app.key}>
            <Paper
              elevation={3}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: 3,
                cursor: 'pointer',
                borderRadius: '11px',
                background: `linear-gradient(180deg, ${THEME_COLORS.panel2}, ${THEME_COLORS.panel})`,
                border: `1px solid ${THEME_COLORS.bezel}`,
                boxShadow: `inset 0 1px 0 ${THEME_COLORS.bezelHi}, inset 0 0 22px rgba(0,0,0,.6), 0 6px 18px rgba(0,0,0,.5)`,
                position: 'relative',
                transition: 'transform .12s, box-shadow .2s, border-color .2s',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 0,
                  height: '3px',
                  borderRadius: '11px 11px 0 0',
                  background: THEME_COLORS.cyan,
                  boxShadow: `0 0 12px ${THEME_COLORS.cyanGlow}`,
                },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  borderColor: THEME_COLORS.bezelHi,
                  boxShadow: `inset 0 1px 0 ${THEME_COLORS.bezelHi}, inset 0 0 22px rgba(0,0,0,.6), 0 8px 22px rgba(0,0,0,.6), 0 0 14px rgba(57,224,255,0.15)`,
                },
              }}
              onClick={app.onClick}
            >
              {app.badge ? (
                <Box
                  sx={{
                    width: 128,
                    height: 128,
                    borderRadius: '24px',
                    background: `radial-gradient(circle at 38% 32%, ${THEME_COLORS.panel2}, #05090b)`,
                    border: `2px solid ${THEME_COLORS.bezel}`,
                    boxShadow: `inset 0 0 14px #000, 0 0 22px ${app.badge.color}55`,
                    color: app.badge.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 48,
                    fontWeight: 700,
                    fontFamily: FONT_DISPLAY,
                    textShadow: `0 0 14px ${app.badge.color}88`,
                  }}
                >
                  {app.badge.text}
                </Box>
              ) : (
                <img
                  src={app.logo}
                  alt={`${app.name} Logo`}
                  style={{ width: 128, height: 128 }}
                />
              )}
              <Typography
                variant="h6"
                sx={{
                  mt: 2,
                  fontFamily: FONT_UI,
                  fontWeight: 700,
                  color: THEME_COLORS.ink,
                  textAlign: 'center',
                }}
              >
                {app.name}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default AppSelection;
